import React, { useState, useEffect } from "react";
import { Tab, Paper } from "@material-ui/core";
import type { Row } from "react-table";
import { IssueSeverity } from "App/Modules/Models";
import type { IResponseWithLoadingAndTimestamp, IIssue } from "App/Modules/Models";
import type { ISiteVideoStreamsRepository } from "App/Modules/Repositories";
import { faHome, faHeartbeat } from "@fortawesome/pro-light-svg-icons";
import {
    faIcon,
    hasUserAccessToSiteInformation,
    hasUserAccessToMonitoring,
    getUserProfile,
    VerticalTabsContainer,
    VerticalTabs,
} from "App/ReactComponents/Shared";
import { CameraImages } from "../CameraImages";
import type { CombinedSite } from "../../Models";
import { IssuesList } from "../IssuesList";
import { useSitesListStyles } from "./SitesListStyles";

type DetailsTab = "overview" | "monitoring";

export const SitesListUIDetailsPanel = ({
    row,
    sitesListMode,
    sitesLoadingState,
    siteVideoStreamsRepository,
    updateIssues,
    showOnlyCriticalIssues,
    showOnlyActiveIssues,
    showOnlyUnacknowledgedIssues,
}: {
    row: Row<CombinedSite>;
    sitesListMode: DetailsTab;
    sitesLoadingState: IResponseWithLoadingAndTimestamp<void>;
    siteVideoStreamsRepository: ISiteVideoStreamsRepository;
    updateIssues: (...issues: Array<Partial<IIssue>>) => void;
    showOnlyCriticalIssues: boolean;
    showOnlyActiveIssues: boolean;
    showOnlyUnacknowledgedIssues: boolean;
}) => {
    const profile = getUserProfile();
    const showSiteInformation = hasUserAccessToSiteInformation(profile);
    const showMonitoring = hasUserAccessToMonitoring(profile);
    const [filteredIssues, setFilteredIssues] = useState(row.original.issues);
    const [currentTab, setCurrentTab] = useState<DetailsTab>(sitesListMode);
    const classes = useSitesListStyles();

    useEffect(() => {
        if (showOnlyCriticalIssues && showOnlyActiveIssues && showOnlyUnacknowledgedIssues) {
            setFilteredIssues(
                row.original.issues.filter(
                    (i) => i.severity === IssueSeverity.Error && i.isActive && !i.isAcknowledged,
                ),
            );
        } else if (showOnlyCriticalIssues && showOnlyActiveIssues && !showOnlyUnacknowledgedIssues) {
            setFilteredIssues(row.original.issues.filter((i) => i.severity === IssueSeverity.Error && i.isActive));
        } else if (showOnlyCriticalIssues && !showOnlyActiveIssues && !showOnlyUnacknowledgedIssues) {
            setFilteredIssues(row.original.issues.filter((i) => i.severity === IssueSeverity.Error));
        } else if (!showOnlyCriticalIssues && showOnlyActiveIssues && !showOnlyUnacknowledgedIssues) {
            setFilteredIssues(row.original.issues.filter((i) => i.isActive));
        } else if (showOnlyCriticalIssues && !showOnlyActiveIssues && showOnlyUnacknowledgedIssues) {
            setFilteredIssues(
                row.original.issues.filter((i) => i.severity === IssueSeverity.Error && !i.isAcknowledged),
            );
        } else if (!showOnlyCriticalIssues && showOnlyActiveIssues && showOnlyUnacknowledgedIssues) {
            setFilteredIssues(row.original.issues.filter((i) => i.isActive && !i.isAcknowledged));
        } else if (!showOnlyCriticalIssues && !showOnlyActiveIssues && showOnlyUnacknowledgedIssues) {
            setFilteredIssues(row.original.issues.filter((i) => !i.isAcknowledged));
        } else {
            setFilteredIssues(row.original.issues);
        }
    }, [showOnlyCriticalIssues, showOnlyActiveIssues, showOnlyUnacknowledgedIssues, row.original.issues]);

    useEffect(() => {
        setCurrentTab(sitesListMode);
    }, [sitesListMode]);

    return showSiteInformation || showMonitoring ? (
        <VerticalTabsContainer>
            <VerticalTabs
                className={classes.verticalTab}
                indicatorColor="secondary"
                textColor="inherit"
                value={currentTab}
                onChange={(event: React.ChangeEvent<object>, newTab: DetailsTab) => {
                    setCurrentTab(newTab);
                }}
            >
                <Tab icon={faIcon(faHome, { size: "2x" })} label="Overview" value="overview" />
                {showMonitoring && (
                    <Tab icon={faIcon(faHeartbeat, { size: "2x" })} label="Monitoring" value="monitoring" />
                )}
            </VerticalTabs>
            <Paper elevation={0} className={classes.sitesListDetailPanelMainContent}>
                <div hidden={currentTab !== "overview"} className={classes.sitesListDetailPanelCameraImagesContainer}>
                    <CameraImages siteVideoStreamsRepository={siteVideoStreamsRepository} site={row.original} />
                </div>
                <div hidden={currentTab !== "monitoring"}>
                    <IssuesList
                        issues={filteredIssues}
                        loadingState={sitesLoadingState}
                        site={row.original}
                        updateIssues={(...issues) => updateIssues(...issues)}
                    />
                </div>
            </Paper>
        </VerticalTabsContainer>
    ) : null;
};
